// Mirror of the server's Workflow/Step enums (app.surveydesk.workflow). Labels: steps.<CODE>.

export const STEP_KIND = {
    SURVEYED: 'DATE',
    SUBMITTED: 'DATE',
    CERTIFICATE: 'CHECK',
    DECISION: 'CHECK_PDF',
    HANDED_OVER: 'CHECK',
    CLIENT_RECEIPT: 'CHECK_PDF',
    STAKED: 'DATE',
    STAKING_SKETCH: 'IMAGE',
    VISITS: 'VISITS',
    SITE_PLAN_SKETCH: 'PDF',
    REPORT_PDF: 'PDF',
    PERMIT_PARCELLING: 'CHECK',
    PERMIT_CERTIFICATE: 'CHECK',
    PERMIT_DECLARATION: 'CHECK',
    PERMIT_SITE_PLAN: 'CHECK',
    PERMIT_PACKAGE: 'PDF',
    ELABORATE_PDF: 'PDF',
    ELABORATE_PREPARED: 'DATE',
    ELABORATE_HANDED_OVER: 'DATE',
};

// field = the step for the work on site: the planned date is for it, and the planner counts the job as done once
// it is ticked (server: Workflow.fieldStep). No field step = a plain planned date. investor = has an Investitor field.
// parcels = has parent parcel / newly formed parcel numbers (subdivision, merger).
export const WORKFLOWS = {
    PARCELLING: { cadastre: true, field: 'SURVEYED', parcels: true, steps: ['SURVEYED', 'SUBMITTED', 'CERTIFICATE', 'DECISION'] },
    PARCELLING_DUP: { cadastre: true, field: 'SURVEYED', parcels: true, steps: ['SURVEYED', 'SUBMITTED', 'CERTIFICATE', 'DECISION', 'HANDED_OVER'] },
    BUILDING_SURVEY: { cadastre: true, field: 'SURVEYED', investor: true, steps: ['SURVEYED', 'SUBMITTED', 'CERTIFICATE', 'CLIENT_RECEIPT'] },
    BUILDING_SURVEY_PERMIT: { cadastre: true, field: 'SURVEYED', investor: true, steps: ['SURVEYED', 'SUBMITTED', 'CERTIFICATE', 'CLIENT_RECEIPT'] },
    APARTMENT_UNITS: { cadastre: true, field: 'SURVEYED', investor: true, steps: ['SURVEYED', 'SUBMITTED', 'CERTIFICATE', 'CLIENT_RECEIPT'] },
    PARCEL_STAKING: { cadastre: false, field: 'STAKED', steps: ['STAKED', 'STAKING_SKETCH'] },
    BUILDING_STAKING: { cadastre: false, steps: ['VISITS'] },
    SITE_PLAN: { cadastre: false, field: 'SURVEYED', steps: ['SURVEYED', 'SITE_PLAN_SKETCH'] },
    REPORT: { cadastre: false, steps: ['REPORT_PDF'] },
    BUILDING_PERMIT: { cadastre: false, field: 'PERMIT_SITE_PLAN', steps: ['PERMIT_PARCELLING', 'PERMIT_CERTIFICATE', 'PERMIT_DECLARATION', 'PERMIT_SITE_PLAN', 'PERMIT_PACKAGE'] },
    PARCEL_MERGE: { cadastre: true, parcels: true, steps: ['ELABORATE_PREPARED', 'ELABORATE_PDF', 'SUBMITTED'] },
    BUILDING_REMOVAL: { cadastre: true, steps: ['ELABORATE_PREPARED', 'ELABORATE_PDF', 'SUBMITTED'] },
    CABLE_ROUTE: { cadastre: false, field: 'SURVEYED', steps: ['SURVEYED', 'ELABORATE_PDF', 'ELABORATE_HANDED_OVER'] },
    PIPE_ROUTE: { cadastre: false, field: 'SURVEYED', steps: ['SURVEYED', 'ELABORATE_PDF', 'ELABORATE_HANDED_OVER'] },
    TEMP_BUILDING_MUNI: { cadastre: false, field: 'SURVEYED', steps: ['SURVEYED', 'ELABORATE_PDF', 'ELABORATE_HANDED_OVER'] },
    TEMP_BUILDING_SEA: { cadastre: false, field: 'SURVEYED', steps: ['SURVEYED', 'ELABORATE_PDF', 'ELABORATE_HANDED_OVER'] },
};

export const workflowOf = (category) => (category?.workflow ? WORKFLOWS[category.workflow] : null);

export const isCadastre = (category) => !!workflowOf(category)?.cadastre;

export const fieldStepOf = (category) => workflowOf(category)?.field || null;

export const hasInvestor = (category) => !!workflowOf(category)?.investor;

export const hasParcelNumbers = (category) => !!workflowOf(category)?.parcels;

/** Label of the planned date: the category's field step ("Planned field survey date"), else the plain one. */
export const scheduledLabel = (t, category) => {
    const step = fieldStepOf(category);
    return step ? t(`jobs.fields.scheduledField.${step}`) : t('jobs.fields.scheduledDate');
};

/** Steps that count for this job (subdivision is optional in building permit work). */
export const activeSteps = (job) => {
    const wf = workflowOf(job?.category);
    if (!wf) return [];
    return job.category.workflow === 'BUILDING_PERMIT' && !job.includeParcelling
        ? wf.steps.filter(s => s !== 'PERMIT_PARCELLING')
        : wf.steps;
};

/** Same payload shape as the job form, so a single field can be changed with a full update. */
export const jobPayload = (job, patch = {}) => ({
    clientId: job.client.id,
    number: job.number,
    categoryId: job.category?.id ?? null,
    title: job.title,
    description: job.description,
    status: job.status,
    scheduledDate: job.scheduledDate,
    scheduledStart: job.scheduledStart,
    scheduledEnd: job.scheduledEnd,
    processedOn: job.processedOn,
    price: job.price,
    parcelNumber: job.parcelNumber,
    cadastralZone: job.cadastralZone,
    municipality: job.municipality,
    ownerName: job.ownerName,
    coOwners: job.coOwners,
    propertySheet: job.propertySheet,
    investor: job.investor,
    parentParcel: job.parentParcel,
    newParcels: job.newParcels,
    latitude: job.latitude,
    longitude: job.longitude,
    cadastreCaseNumber: job.cadastreCaseNumber,
    submittedOn: job.steps?.SUBMITTED ?? null,
    includeParcelling: job.includeParcelling,
    ...patch,
});
